import { buildCards } from "@/lib/vzCards";
import { KpiCard } from "./KpiCallout";
import { Delta } from "./SalesKpis";

const DELTA_KEY = { acts: "current_acts", upgrades: "current_upgrades", edge: "current_edge_acts",
  topups: "current_topups", fwa: "current_fwa_acts", tablets: "current_tablets" };


const dot = (c) => {
  if (!c.quota) return "bg-slate-300";
  return (c.track || 0) >= c.quota ? "bg-emerald-500" : "bg-red-500";
};

const colSlug = (l) => l.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-$/, "");

const SHORT = { Current: "Cur", Surveys: "Surv", Promo: "Prom", Detract: "Det" };
const short = (v) => (typeof v === "string" && /^-?\d+(\.\d+)?%$/.test(v) ? `${Math.round(parseFloat(v))}%` : v);
// full text on very wide screens, compact text otherwise so 4 cards fit per row
const Fit = ({ full, compact }) => (full === compact ? full : (
  <><span className="2xl:hidden">{compact}</span><span className="hidden 2xl:inline">{full}</span></>
));

export const StoreLines = ({ cardKey, perStore, testBase }) => {
  const labels = (perStore[0]?.cards[cardKey].line || []).map(([l]) => l);
  const cell = "px-[3px] 2xl:px-1 text-center tabular-nums whitespace-nowrap border-l border-slate-200";
  return (
    <div className="overflow-hidden mt-2 border-t border-slate-100">
    <table className="vz-compact w-full leading-4" data-testid={`${testBase}-stores`}>
      <thead>
        <tr className="text-[9px] uppercase tracking-tight text-slate-400">
          <th className="text-left font-medium py-1 pr-0.5">Store</th>
          {labels.map((l) => <th key={l} className={`${cell} font-medium py-1`}><Fit full={l} compact={SHORT[l] || l} /></th>)}
        </tr>
      </thead>
      <tbody>
        {perStore.map(({ store, cards }) => {
          const c = cards[cardKey];
          return (
            <tr key={store.door_tsp} data-testid={`${testBase}-store-${store.door_tsp}`}>
              <td className="py-0.5 pr-0.5 text-slate-600" title={`${store.store_id} · ${store.address}`}>
                <span className="flex items-center gap-1 min-w-0">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot(c)}`} data-testid={`${testBase}-store-${store.door_tsp}-dot`} />
                  <span className="whitespace-nowrap">{store.store_id}</span>
                </span>
              </td>
              {c.line.map(([l, v], i) => (
                <td key={l} className={`${cell} py-0.5 ${i === 0 ? "font-bold text-navy" : "text-slate-500"}`}
                  data-testid={`${testBase}-store-${store.door_tsp}-${colSlug(l)}`}><Fit full={v} compact={short(v)} /></td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
};

export const buildPerStore = (stores, opts) =>
  [...stores].sort((a, b) => String(a.store_id).localeCompare(String(b.store_id))).map((store) => {
    const r = buildCards({ ...opts, scope: [store] });
    return { store, cards: Object.fromEntries([...r.row1, ...r.row2, ...r.row3].map((c) => [c.key, c])) };
  });

export const DealerSalesCallouts = ({ summary }) => {
  const live = !summary.period || summary.period.is_current;
  const opts = { summaryTotals: summary.totals, qs: summary.quota_settings, live, mr: true };
  const all = buildCards({ ...opts, scope: summary.stores });
  const perStore = buildPerStore(summary.stores, opts);
  const cmp = summary.compare;
  return (
    <div className="space-y-4" data-testid="vz-callouts">
      {[all.row1, all.row2, all.row3].map((cards, row) => (
        <div key={row} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 2xl:gap-3" data-testid={`vz-callout-row-${row + 1}`}>
          {cards.map((card, i) => (
            <KpiCard key={card.key} card={{ ...card, details: null, detailTable: null }} index={i} prefix="vzc">
              {cmp && DELTA_KEY[card.key] && <Delta cur={summary.totals[DELTA_KEY[card.key]]} prev={cmp.totals?.[DELTA_KEY[card.key]]} label={cmp.label} testId={`vzc-${card.key}-delta`} />}
              <StoreLines cardKey={card.key} perStore={perStore} testBase={`vzc-${card.key}`} />
            </KpiCard>
          ))}
        </div>
      ))}
    </div>
  );
};
